<template>
  <div>
    <UDropdownMenu
      v-if="account"
      :items="menuItems"
      :content="{ side: 'right', align: 'end', sideOffset: 10, collisionPadding: 12 }"
      :external-icon="false"
      :ui="{
        content: 'w-[min(22rem,calc(100vw-1rem))]',
      }"
    >
      <button
        type="button"
        class="flex w-full items-center justify-between gap-3 rounded-lg px-1 py-1 text-white/95 transition-colors cursor-pointer hover:bg-white/10"
      >
        <div class="flex min-w-0 items-center">
          <img
            v-if="accountImage"
            :src="accountImage"
            class="inline-block h-7 w-7 rounded-full text-white mr-3 shrink-0"
            :class="[
              collapse ? 'ml-px' : ' ml-1',
            ]"
          >

          <div
            v-if="!accountImage"
            class="inline-flex h-7 w-7 rounded-full text-white mr-3 items-center justify-center bg-white/15 shrink-0"
            :class="[
              collapse ? 'ml-px' : ' ml-1',
            ]"
          >
            <span>{{ account.employee.initials }}</span>
          </div>

          <div :class="[ collapse ? 'sm:hidden' : '' ]" class="min-w-0 text-left leading-none opacity-95">
            <p class="truncate leading-none font-medium text-base">
              {{ account.employee.firstName }}
              {{ account.employee.lastName }}
            </p>
          </div>
        </div>

        <o-icon :class="[ collapse ? 'sm:hidden' : 'shrink-0' ]" icon="accordion" :size="14" />
      </button>

      <template #user>
        <div class="flex items-center gap-4 text-left">
          <img
            v-if="accountImage"
            :src="accountImage"
            class="h-10 w-10 rounded-full border border-gray-200 dark:border-gray-800"
          >
          <div
            v-else
            class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-100"
          >
            <span>{{ account.employee.initials }}</span>
          </div>
          <div>
            <p class="text-base font-semibold text-gray-900 dark:text-white">
              {{ account.employee.firstName }} {{ account.employee.lastName }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-300">
              {{ account.location.title }} - #{{ account.location.idLocation }}
            </p>
          </div>
        </div>
      </template>

      <template #language-trailing>
        <span class="text-xs font-medium text-gray-500 dark:text-gray-400">
          {{ currentLang?.icon }} {{ currentLang?.name }}
        </span>
      </template>

      <template #locale-label="{ item }">
        <span class="flex w-full items-center justify-between gap-3">
          <span>{{ item.localeIcon }} {{ item.localeName }}</span>
          <UIcon
            v-if="currentLang?.code === item.localeCode"
            name="lucide:check"
            class="size-4 text-primary-500"
          />
        </span>
      </template>

      <template #theme-trailing>
        <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ currentThemeLabel }}</span>
      </template>

      <template #system-mode-label>
        <span class="flex w-full items-center justify-between gap-3">
          <span>{{ $t('components.layout.account.system') }}</span>
          <UIcon
            v-if="$colorMode.preference === 'system'"
            name="lucide:check"
            class="size-4 text-primary-500"
          />
        </span>
      </template>

      <template #light-mode-label>
        <span class="flex w-full items-center justify-between gap-3">
          <span>{{ $t('theme.light') }}</span>
          <UIcon
            v-if="$colorMode.preference === 'light'"
            name="lucide:check"
            class="size-4 text-primary-500"
          />
        </span>
      </template>

      <template #dark-mode-label>
        <span class="flex w-full items-center justify-between gap-3">
          <span>{{ $t('theme.dark') }}</span>
          <UIcon
            v-if="$colorMode.preference === 'dark'"
            name="lucide:check"
            class="size-4 text-primary-500"
          />
        </span>
      </template>

      <template #remote-events-label>
        <span class="flex min-w-0 flex-col gap-1">
          <span class="font-medium">{{ $t('components.layout.account.remoteEvents') }}</span>

          <span v-if="$centrifuge.instance" class="space-y-1 text-xs text-gray-500 dark:text-gray-400">
            <span class="block">
              {{ $t('components.layout.account.client', { label: $centrifuge.instance._client }) }}
            </span>
            <i18n-t
              keypath="components.layout.account.subs"
              tag="span"
              class="block"
              scope="global"
            >
              <template #keys>
                <span v-for="(sub, key) in $centrifuge.instance._serverSubs" :key="key">{{ key }}</span>
              </template>
            </i18n-t>
          </span>

          <span v-else class="text-xs text-gray-500 dark:text-gray-400">
            {{ $t('components.layout.account.notConnected') }}
          </span>
        </span>
      </template>
    </UDropdownMenu>

    <ChangePasswordDialog
      ref="changePasswordDialogRef"
    />

    <ChangePinDialog
      ref="changePinDialogRef"
      @success="handleChangePinSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useNuxtApp, useRuntimeConfig } from '#imports'
import ChangePasswordDialog from '@/components/auth/actions/ChangePassword.vue'
import ChangePinDialog from '@/components/auth/actions/ChangePin.vue'
import { useUIStore } from '~/stores/useUIStore'

const { $auth, $i18n, $colorMode, $grants, $centrifuge, $panel } = useNuxtApp()
const runtimeConfig = useRuntimeConfig()
const uiStore = useUIStore()

const changePasswordDialogRef = ref()
const changePinDialogRef = ref()

const locales = computed(() => {
  const raw = $i18n.locales
  return Array.isArray(raw) ? raw : (raw?.value ?? [])
})

const collapse = computed(() => uiStore.menu)
const account = computed(() => $auth.info)
const demo = computed(() => runtimeConfig.public.instance === 'demo')
const planLabel = computed(() => account.value?.plan?.infoLabel || '')
const isDev = computed(() => runtimeConfig.public.instance === 'dev')
const isAdmin = computed(() => $auth?.info?.adminUser)
const canViewHistory = computed(() => $grants.all({ history: ['view'] }))
const accountImage = computed(() => resolveImage(
  account.value?.employee?.image ?? account.value?.employee?.images,
))
const historyPath = computed(() => {
  const locationId = account.value?.location?.idLocation

  if (!locationId) {
    return null
  }

  return `/history?modelType=Location&primaryKey=${locationId}`
})
const currentLang = computed(() => {
  const currentCode = $i18n.locale?.value ?? $i18n.locale
  return locales.value.find(locale => locale.code === currentCode)
})

const currentThemeLabel = computed(() => {
  const mode = $colorMode.value
  if (mode === 'system') {
    return $i18n.t('components.layout.account.system')
  }
  if (mode === 'light') {
    return $i18n.t('theme.light')
  }
  if (mode === 'dark') {
    return $i18n.t('components.layout.account.darkAuto')
  }
  return ''
})

const handleLock = () => $auth.lock()
const handleSwitch = () => $auth.switch()
const handleLogout = () => $auth.logout()

const handleChangePassword = () => {
  changePasswordDialogRef.value?.open()
}

const handleChangePin = () => {
  changePinDialogRef.value?.open()
}

const handleAccountHistory = () => {
  if (!historyPath.value) return
  ;($panel as any).create(historyPath.value)
}

const handleChangePinSuccess = () => {
  $auth.syncActive()
}

const handleSetColorMode = (mode) => {
  $colorMode.preference = mode
}

const handleSetLocale = (lang) => {
  $i18n.setLocale(lang)
}

const menuItems = computed(() => {
  const items = []

  // Lock & Switch section
  const userGroup = [
    {
      slot: 'user',
    }
  ]

  if (account.value?.employee?.pinHash) {
    userGroup.push({
      label: $i18n.t('components.layout.account.lockAccount'),
      icon: 'lucide:lock',
      onSelect: handleLock,
    })
  }
  if (!demo.value) {
    userGroup.push({
      label: $i18n.t('components.layout.account.switchUser'),
      icon: 'lucide:arrow-left-right',
      onSelect: handleSwitch,
    })
  }

  items.push(userGroup)


  if ((isAdmin.value || isDev.value) && canViewHistory.value && historyPath.value) {
    items.push([
      {
        type: 'label',
        label: $i18n.t('role.admin'),
      },
      {
        label: $i18n.t('components.layout.account.accountHistory'),
        icon: 'lucide:history',
        onSelect: handleAccountHistory,
      },
    ])
  }

  // Plan, Settings, Help, Updates section
  const mainGroup = []
  if (!demo.value && $grants.all({ account: ['billing'] })) {
    mainGroup.push({
      label: $i18n.t('components.layout.account.plan'),
      description: planLabel.value,
      icon: 'lucide:credit-card',
      to: '/account',
    })
  }
  if ($grants.all({ account: ['edit'] })) {
    mainGroup.push({
      label: $i18n.t('general.settings'),
      description: $i18n.t('components.layout.account.manageAccountSettings'),
      icon: 'lucide:settings',
      to: '/account',
    })
  }
  mainGroup.push({
    label: $i18n.t('components.layout.help'),
    description: $i18n.t('components.layout.account.guidesAndFAQs'),
    icon: 'lucide:life-buoy',
    href: 'https://ovatu.com/au/help/guides',
    target: '_blank',
  })
  mainGroup.push({
    label: $i18n.t('general.updates'),
    description: $i18n.t('components.layout.account.featuresAndChanges'),
    icon: 'lucide:megaphone',
    href: 'https://ovatu.com/au/changelog',
    target: '_blank',
  })
  items.push(mainGroup)

  // Language & Theme submenus
  const prefsGroup = []

  // Language submenu
  const localeChildren = locales.value.map(locale => ({
    label: locale.name,
    slot: 'locale',
    localeIcon: locale.icon,
    localeName: locale.name,
    localeCode: locale.code,
    onSelect: () => handleSetLocale(locale.code),
  }))
  prefsGroup.push({
    label: $i18n.t('i18n.language'),
    description: $i18n.t('i18n.description'),
    slot: 'language',
    icon: 'lucide:languages',
    children: localeChildren,
  })

  // Theme submenu
  prefsGroup.push({
    label: $i18n.t('general.theme'),
    slot: 'theme',
    icon: 'lucide:palette',
    children: [
      {
        label: $i18n.t('components.layout.account.system'),
        slot: 'system-mode',
        icon: 'lucide:monitor',
        onSelect: () => handleSetColorMode('system'),
      },
      {
        label: $i18n.t('theme.light'),
        slot: 'light-mode',
        icon: 'lucide:sun',
        onSelect: () => handleSetColorMode('light'),
      },
      {
        label: $i18n.t('theme.dark'),
        slot: 'dark-mode',
        icon: 'lucide:moon',
        onSelect: () => handleSetColorMode('dark'),
      },
    ],
  })
  items.push(prefsGroup)

  // Password & PIN
  items.push([
    {
      label: $i18n.t('auth.password.changePassword.title'),
      icon: 'lucide:key-round',
      onSelect: handleChangePassword,
    },
    {
      label: $i18n.t('auth.pin.change'),
      icon: 'lucide:hash',
      onSelect: handleChangePin,
    },
  ])

  // Admin section & Logout
  const lastGroup = []
  if (isAdmin.value || isDev.value) {
    lastGroup.push({
      label: $i18n.t('components.layout.account.remoteEvents'),
      slot: 'remote-events',
      icon: 'lucide:radio',
    })
  }
  if (!demo.value) {
    lastGroup.push({
      label: $i18n.t('components.layout.account.logoutUser'),
      icon: 'lucide:log-out',
      onSelect: handleLogout,
    })
  }
  if (demo.value) {
    lastGroup.push({
      label: $i18n.t('components.layout.account.leaveDemo'),
      icon: 'lucide:log-out',
      href: 'https://ovatu.com/plans',
    })
  }
  if (lastGroup.length) {
    items.push(lastGroup)
  }

  return items
})

function resolveImage (image) {
  if (typeof image === 'string' && image.length) {
    return image
  }

  if (image && typeof image === 'object') {
    const source = image
    const value = source.square ?? source.medium ?? source.large ?? source.url ?? source.src
    if (typeof value === 'string' && value.length) {
      return value
    }
  }

  return null
}
</script>
